/**
 * Sector Operations — Delivery Change Requests, Finance, Client Approvers & UAT.
 * R39 E2.a + E2.b (plan docs/plans/sector-operations.md SO-P269, SO-D36, SO-D47).
 */

import { validateIsoCurrency } from './delivery.js'

// ---------------------------------------------------------------------------
// Client Approvers (SO-D47, SO-P269)
// ---------------------------------------------------------------------------

export interface OpsClientApprover {
  id: string
  name: string
  phone: string
}

export interface OpsEngagementContacts {
  workspaceId: string
  engagementId: string
  clientApprovers: OpsClientApprover[]
  updatedAt: string
  updatedBy: string
}

const E164_PHONE_REGEX = /^\+[1-9]\d{6,14}$/

export function validateEngagementClientApprovers(approvers: unknown): OpsClientApprover[] {
  if (!Array.isArray(approvers)) {
    throw new Error('clientApprovers must be an array.')
  }
  if (approvers.length < 1 || approvers.length > 3) {
    throw new Error('clientApprovers must contain between 1 and 3 approvers.')
  }

  const result: OpsClientApprover[] = []
  const seenPhones = new Set<string>()
  const seenIds = new Set<string>()

  for (let i = 0; i < approvers.length; i++) {
    const item = approvers[i]
    if (!item || typeof item !== 'object') {
      throw new Error(`Approver at index ${i} must be an object.`)
    }
    const raw = item as Record<string, unknown>

    const id = typeof raw.id === 'string' ? raw.id.trim() : ''
    if (!id || id.length > 120) {
      throw new Error(`Approver at index ${i} must have an id (1–120 characters).`)
    }
    if (seenIds.has(id)) {
      throw new Error(`Duplicate approver id: "${id}".`)
    }
    seenIds.add(id)

    const name = typeof raw.name === 'string' ? raw.name.trim() : ''
    if (name.length < 2 || name.length > 80) {
      throw new Error(`Approver at index ${i} must have a name between 2 and 80 characters.`)
    }

    const phone = typeof raw.phone === 'string' ? raw.phone.trim() : ''
    if (!E164_PHONE_REGEX.test(phone)) {
      throw new Error(`Approver "${name}" must have a valid E.164 phone number (e.g. +919876543210).`)
    }
    if (seenPhones.has(phone)) {
      throw new Error(`Duplicate phone number across client approvers: "${phone}". Distinct phones are required.`)
    }
    seenPhones.add(phone)

    result.push({ id, name, phone })
  }

  return result
}

// ---------------------------------------------------------------------------
// Change Requests (SO-P269)
// ---------------------------------------------------------------------------

export type OpsChangeRequestStatus =
  | 'DRAFT'
  | 'SENT'
  | 'APPROVED'
  | 'REJECTED'
  | 'REVISION_REQUESTED'
  | 'WITHDRAWN'

export const OPS_CHANGE_REQUEST_STATUSES: readonly OpsChangeRequestStatus[] = [
  'DRAFT',
  'SENT',
  'APPROVED',
  'REJECTED',
  'REVISION_REQUESTED',
  'WITHDRAWN',
] as const

export interface OpsChangeRequest {
  id: string
  workspaceId: string
  engagementId: string
  clientId: string
  number: number
  title: string
  whatChanges: string
  hoursEstimate: number
  release?: string | null
  linkedNodeIds: string[]
  status: OpsChangeRequestStatus
  currentVersion: number
  approvalId?: string | null
  unbilledHours: number
  sentAt?: string | null
  decidedAt?: string | null
  decidedByName?: string | null
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
}

export function formatChangeRequestNumber(num: number): string {
  if (!Number.isInteger(num) || num <= 0) {
    return `CR-${String(num)}`
  }
  return `CR-${String(num).padStart(3, '0')}`
}

export function validateChangeRequestTitle(title: unknown): string {
  if (typeof title !== 'string') {
    throw new Error('Title must be a string.')
  }
  const trimmed = title.trim()
  if (trimmed.length < 3 || trimmed.length > 120) {
    throw new Error('Title must be between 3 and 120 characters.')
  }
  return trimmed
}

export function validateChangeRequestWhatChanges(what: unknown): string {
  if (typeof what !== 'string') {
    throw new Error('whatChanges must be a string.')
  }
  const trimmed = what.trim()
  if (trimmed.length < 3 || trimmed.length > 2000) {
    throw new Error('whatChanges must be between 3 and 2000 characters.')
  }
  return trimmed
}

export function validateChangeRequestHours(hours: unknown): number {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) {
    throw new Error('hoursEstimate must be a number.')
  }
  if (hours < 0.5 || hours > 10000) {
    throw new Error('hoursEstimate must be between 0.5 and 10000.')
  }
  // Steps of 0.5
  if (Math.round(hours * 2) !== hours * 2) {
    throw new Error('hoursEstimate must be in steps of 0.5 hours.')
  }
  return hours
}

const RELEASE_TAG_REGEX = /^[A-Za-z0-9._-]+$/

export function validateChangeRequestReleaseTag(release: unknown): string | null {
  if (release === null || release === undefined || release === '') {
    return null
  }
  if (typeof release !== 'string') {
    throw new Error('Release tag must be a string or null.')
  }
  const trimmed = release.trim()
  if (!trimmed) return null
  if (trimmed.length < 1 || trimmed.length > 20) {
    throw new Error('Release tag must be between 1 and 20 characters.')
  }
  if (!RELEASE_TAG_REGEX.test(trimmed)) {
    throw new Error('Release tag must match [A-Za-z0-9._-].')
  }
  return trimmed
}

export function validateChangeRequestLinkedNodeIds(nodeIds: unknown): string[] {
  if (nodeIds === null || nodeIds === undefined) {
    return []
  }
  if (!Array.isArray(nodeIds)) {
    throw new Error('linkedNodeIds must be an array.')
  }
  if (nodeIds.length > 20) {
    throw new Error('linkedNodeIds can have at most 20 items.')
  }
  const result: string[] = []
  const seen = new Set<string>()
  for (let i = 0; i < nodeIds.length; i++) {
    const raw = nodeIds[i]
    if (typeof raw !== 'string') {
      throw new Error(`linkedNodeIds[${i}] must be a string.`)
    }
    const trimmed = raw.trim()
    if (!trimmed || trimmed.length > 120) {
      throw new Error(`linkedNodeIds[${i}] must be between 1 and 120 characters.`)
    }
    if (!seen.has(trimmed)) {
      seen.add(trimmed)
      result.push(trimmed)
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Price Effect (SO-P269, Q5)
// ---------------------------------------------------------------------------

export type OpsChangeRequestPriceEffect =
  | { kind: 'AMOUNT'; amount: number }
  | { kind: 'TIME_AND_MATERIALS' }
  | { kind: 'NONE' }

export interface OpsChangeRequestFinance {
  workspaceId: string
  engagementId: string
  crId: string
  priceEffect: OpsChangeRequestPriceEffect
  currency: string
  updatedAt: string
  updatedBy: string
}

export function validateChangeRequestPriceEffect(
  effect: unknown,
  currency?: string,
): OpsChangeRequestPriceEffect {
  if (!effect || typeof effect !== 'object') {
    throw new Error('priceEffect must be an object.')
  }
  const raw = effect as Record<string, unknown>
  const kind = raw.kind
  if (kind === 'TIME_AND_MATERIALS') {
    return { kind: 'TIME_AND_MATERIALS' }
  }
  if (kind === 'NONE') {
    return { kind: 'NONE' }
  }
  if (kind === 'AMOUNT') {
    const amount = raw.amount
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
      throw new Error('Price effect amount must be a number greater than 0.')
    }
    if (currency !== undefined) {
      validateIsoCurrency(currency)
    }
    return { kind: 'AMOUNT', amount }
  }
  throw new Error(`Invalid priceEffect kind: "${String(kind)}". Must be AMOUNT, TIME_AND_MATERIALS, or NONE.`)
}

export function formatChangeRequestPriceDisplay(
  priceEffect?: OpsChangeRequestPriceEffect | null,
  currency?: string,
): string {
  if (!priceEffect || priceEffect.kind === 'NONE') {
    return 'None'
  }
  if (priceEffect.kind === 'TIME_AND_MATERIALS') {
    return 'Time and materials'
  }
  if (priceEffect.kind === 'AMOUNT') {
    const prefix = '+'
    const curr = currency ? `${currency} ` : ''
    return `${prefix}${curr}${priceEffect.amount.toLocaleString('en-US')}`
  }
  return 'None'
}

// ---------------------------------------------------------------------------
// Change Request Versions (SO-P269)
// ---------------------------------------------------------------------------

export type OpsChangeRequestVersionOutcome =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'REVISION_REQUESTED'
  | 'WITHDRAWN'

export const OPS_CHANGE_REQUEST_VERSION_OUTCOMES: readonly OpsChangeRequestVersionOutcome[] = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'REVISION_REQUESTED',
  'WITHDRAWN',
] as const

export interface OpsChangeRequestVersion {
  version: number
  crId: string
  workspaceId: string
  engagementId: string
  title: string
  whatChanges: string
  hoursEstimate: number
  release?: string | null
  priceDisplay: string
  linkedItemTitles: string[]
  sentAt: string
  sentBy: string
  sentByName: string
  approvalId: string
  outcome: OpsChangeRequestVersionOutcome
  clientComment?: string | null
  decidedAt?: string | null
  decidedByName?: string | null
}

// ---------------------------------------------------------------------------
// UAT Checklists (SO-P269, Q2)
// ---------------------------------------------------------------------------

export interface OpsUatChecklistItem {
  id: string
  text: string
  crId?: string | null
}

export interface OpsUatChecklist {
  id: string
  workspaceId: string
  engagementId: string
  title: string
  version: number
  items: OpsUatChecklistItem[]
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
}

export interface OpsUatChecklistVersion {
  version: number
  checklistId: string
  workspaceId: string
  engagementId: string
  title: string
  items: OpsUatChecklistItem[]
  createdAt: string
  createdBy: string
}

export function validateUatChecklistTitle(title: unknown): string {
  if (typeof title !== 'string') {
    throw new Error('Title must be a string.')
  }
  const trimmed = title.trim()
  if (trimmed.length < 3 || trimmed.length > 120) {
    throw new Error('Checklist title must be between 3 and 120 characters.')
  }
  return trimmed
}

export function validateUatChecklistItems(items: unknown): OpsUatChecklistItem[] {
  if (!Array.isArray(items)) {
    throw new Error('items must be an array.')
  }
  if (items.length < 1 || items.length > 100) {
    throw new Error('Checklist must have between 1 and 100 items.')
  }

  const result: OpsUatChecklistItem[] = []
  const seenIds = new Set<string>()

  for (let i = 0; i < items.length; i++) {
    const raw = items[i]
    if (!raw || typeof raw !== 'object') {
      throw new Error(`Item at index ${i} must be an object.`)
    }
    const item = raw as Record<string, unknown>
    const id = typeof item.id === 'string' ? item.id.trim() : ''
    if (!id || id.length > 120) {
      throw new Error(`Item at index ${i} must have an id (1–120 characters).`)
    }
    if (seenIds.has(id)) {
      throw new Error(`Duplicate checklist item id: "${id}".`)
    }
    seenIds.add(id)

    const text = typeof item.text === 'string' ? item.text.trim() : ''
    if (text.length < 3 || text.length > 200) {
      throw new Error(`Item at index ${i} must have text between 3 and 200 characters.`)
    }

    const crId =
      item.crId !== null && item.crId !== undefined && typeof item.crId === 'string' && item.crId.trim()
        ? item.crId.trim()
        : null

    result.push({ id, text, crId })
  }

  return result
}

// ---------------------------------------------------------------------------
// UAT Rounds (SO-P269, Q2)
// ---------------------------------------------------------------------------

export type OpsUatItemResult = 'NOT_TESTED' | 'PASSED' | 'FAILED' | 'BLOCKED'
export const OPS_UAT_ITEM_RESULTS: readonly OpsUatItemResult[] = [
  'NOT_TESTED',
  'PASSED',
  'FAILED',
  'BLOCKED',
] as const

export type OpsUatRoundStatus =
  | 'OPEN'
  | 'SENT_FOR_SIGNOFF'
  | 'SIGNED_OFF'
  | 'REJECTED'
  | 'CLOSED'
export const OPS_UAT_ROUND_STATUSES: readonly OpsUatRoundStatus[] = [
  'OPEN',
  'SENT_FOR_SIGNOFF',
  'SIGNED_OFF',
  'REJECTED',
  'CLOSED',
] as const

export interface OpsUatRoundItem {
  id: string
  text: string
  crId?: string | null
  result: OpsUatItemResult
  source?: 'STAFF' | 'CLIENT' | null
  note?: string | null
  byName?: string | null
  at?: string | null
}

export interface OpsUatRound {
  id: string
  workspaceId: string
  engagementId: string
  release: string
  checklistId: string
  checklistVersion: number
  roundNumber: number
  items: OpsUatRoundItem[]
  status: OpsUatRoundStatus
  approvalId?: string | null
  createdAt: string
  createdBy: string
  updatedAt: string
  updatedBy: string
  decidedAt?: string | null
  decidedByName?: string | null
}

export function validateUatNote(note: unknown): string | null {
  if (note === null || note === undefined || note === '') {
    return null
  }
  if (typeof note !== 'string') {
    throw new Error('note must be a string or null.')
  }
  const trimmed = note.trim()
  if (!trimmed) return null
  if (trimmed.length > 500) {
    throw new Error('note must be at most 500 characters.')
  }
  return trimmed
}
